import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Upload, ArrowLeft } from 'lucide-react';

const CollectionPage = () => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  
  const getCollectionInfo = () => {
    switch(slug) {
      case 'diamond-sculptures':
        return {
          title: 'Statues',
          subtitle: 'Sculptural masterpieces',
          description: 'Our statue collection showcases exceptional sculptural artistry, featuring timeless pieces that embody elegance, power, and refined craftsmanship.'
        };
      case 'coffee-tables':
        return {
          title: 'Coffee Tables',
          subtitle: 'Functional luxury centerpieces',
          description: 'Transform your living space with our handcrafted coffee tables that blend artistry with functionality, each piece a conversation starter.'
        };
      case 'mixed-art-pieces':
        return {
          title: 'Mixed Art Pieces',
          subtitle: 'Eclectic creative expressions',
          description: 'Explore our diverse collection of mixed media artworks that push boundaries and blend various artistic disciplines into stunning creations.'
        };
      default:
        return {
          title: 'Collection',
          subtitle: 'Our curated pieces',
          description: 'Discover unique artworks and designs.'
        };
    }
  };

  const collectionInfo = getCollectionInfo();

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Back Button */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-[hsl(45,100%,70%)] transition-colors group ml-52"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to home
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in">
            {collectionInfo.title}
          </h1>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed animate-fade-in animation-delay-200">
            {collectionInfo.description}
          </p>
        </div>

        {/* Coming Soon / Upload Notice */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-12 border border-gray-800 text-center animate-fade-in animation-delay-600">
            <div className="w-24 h-24 rounded-full bg-[hsl(45,100%,70%)]/10 flex items-center justify-center mx-auto mb-6">
              <Upload className="w-12 h-12 text-[hsl(45,100%,70%)]" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">
              Collection Coming Soon
            </h2>
            
            <p className="text-gray-400 mb-8 leading-relaxed">
              We're currently curating exceptional pieces for this collection. Our {collectionInfo.title.toLowerCase()} will showcase the finest in luxury craftsmanship and design.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-[hsl(45,100%,70%)] text-black font-bold rounded-full hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] transition-all duration-300 hover:scale-105"
              >
                Get Notified
              </Link>
              
              <Link
                to="/"
                className="px-8 py-4 border-2 border-[hsl(45,100%,70%)] text-[hsl(45,100%,70%)] font-bold rounded-full hover:bg-[hsl(45,100%,70%)] hover:text-black transition-all duration-300 hover:scale-105"
              >
                Explore Other Collections
              </Link>
            </div>
          </div>
        </div>

        {/* Placeholder for future products */}
        {products.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {/* Products will be rendered here when uploaded */}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out both;
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }

        .animation-delay-600 {
          animation-delay: 0.6s;
        }
      `}</style>
    </div>
  );
};

export default CollectionPage;
